import React from 'react';
import Plot from 'react-plotly.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import ChartExportButton from "@/components/shared/ChartExportButton";
import { MetricTile } from '@/components/shared/MetricTile';
import { useIsMobile } from '@/hooks/use-mobile';
import { toTitleCase } from '@/lib/utils';
import {
  plotlyChartChrome,
  recessionAnnotations,
  usePlotlyTheme,
} from '@/lib/plotlyTheme';

interface WageHistory {
  year: number;
  occ_code: string;
  occ_title: string;
  a_mean: number;
  a_median?: number;
  employment: number;
  a_p10?: number;
  a_p25?: number;
  a_p75?: number;
  a_p90?: number;
}

interface EconomicContext {
  year: number;
  area_code: string;
  unemployment_rate?: number;
  gdp_growth?: number;
  recession_flag: boolean;
  recovery_phase?: string;
}

interface InteractiveTimeseriesProps {
  wageHistory: WageHistory[];
  economicContext?: EconomicContext[];
  title?: string;
  showPercentiles?: boolean;
  showEmployment?: boolean;
  rppAdjusted?: boolean;
  occupationTitle?: string;
}

export const InteractiveTimeseries: React.FC<InteractiveTimeseriesProps> = ({
  wageHistory,
  economicContext = [],
  title = "Salary Trends Analysis",
  showPercentiles = false,
  showEmployment = false,
  rppAdjusted = false,
  occupationTitle = "Unknown Occupation"
}) => {
  const isMobile = useIsMobile();
  const { colors, themeKey } = usePlotlyTheme();

  if (!wageHistory || wageHistory.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No trend data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const years = wageHistory.map(d => d.year);
  const derivedOccupationTitle = toTitleCase(wageHistory[0]?.occ_title || occupationTitle);

  // Recession periods for shading
  const recessionYears = economicContext
    .filter(ec => ec.recession_flag)
    .map(ec => ec.year);

  const traces = [];

  // Main salary line
  traces.push({
    x: years,
    y: wageHistory.map(d => d.a_median || d.a_mean),
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Median Salary',
    line: { color: '#6366f1', width: 3 },
    marker: { color: '#6366f1', size: 6 },
    hovertemplate: 
      '<b>%{x}</b><br>' +
      'Median: $%{y:,.0f}<br>' +
      '<extra></extra>'
  });

  // Percentile bands (if enabled)
  if (showPercentiles && wageHistory.some(d => d.a_p10 && d.a_p90)) {
    // P10-P90 band
    traces.push({
      x: [...years, ...years.slice().reverse()],
      y: [
        ...wageHistory.map(d => d.a_p90 || 0),
        ...wageHistory.slice().reverse().map(d => d.a_p10 || 0)
      ],
      fill: 'toself',
      fillcolor: 'rgba(99, 102, 241, 0.1)',
      line: { color: 'transparent' },
      showlegend: false,
      hoverinfo: 'skip',
      name: 'P10-P90 Range'
    });

    // P25-P75 band
    traces.push({
      x: [...years, ...years.slice().reverse()],
      y: [
        ...wageHistory.map(d => d.a_p75 || 0),
        ...wageHistory.slice().reverse().map(d => d.a_p25 || 0)
      ],
      fill: 'toself',
      fillcolor: 'rgba(99, 102, 241, 0.2)',
      line: { color: 'transparent' },
      showlegend: false,
      hoverinfo: 'skip',
      name: 'P25-P75 Range'
    });
  }

  // Employment trend (if enabled)
  if (showEmployment) {
    traces.push({
      x: years,
      y: wageHistory.map(d => d.employment),
      type: 'scatter',
      mode: 'lines',
      name: 'Employment',
      line: { color: '#22c55e', width: 2, dash: 'dot' },
      yaxis: 'y2',
      hovertemplate: 
        '<b>%{x}</b><br>' +
        'Employment: %{y:,.0f}<br>' +
        '<extra></extra>'
    });
  }

  // Recession shading
  recessionYears.forEach(year => {
    const yearData = wageHistory.find(d => d.year === year);
    if (yearData) {
      traces.push({
        x: [year - 0.5, year + 0.5, year + 0.5, year - 0.5],
        y: [0, 0, 1, 1],
        fill: 'toself',
        fillcolor: colors.recessionFill,
        line: { color: 'transparent' },
        showlegend: false,
        hoverinfo: 'skip',
        yaxis: 'y3',
        name: 'Recession'
      });
    }
  });

  const chrome = plotlyChartChrome(colors, { isMobile });

  const layout = {
    title: {
      text: `${derivedOccupationTitle} - Salary Trends${rppAdjusted ? ' (Inflation Adjusted)' : ''}`,
      font: chrome.titleFont
    },
    xaxis: {
      title: chrome.axisTitle(isMobile ? undefined : 'Year'),
      type: 'linear',
      dtick: isMobile ? 5 : 2,
      nticks: isMobile ? 7 : undefined,
      showgrid: true,
      tickangle: isMobile ? -45 : 0,
      ...chrome.axisDefaults
    },
    yaxis: {
      title: chrome.axisTitle(isMobile ? undefined : `Annual Salary${rppAdjusted ? ' (Real)' : ''} ($)`),
      tickformat: '$,.0f',
      showgrid: true,
      ...chrome.axisDefaults
    },
    yaxis2: showEmployment ? {
      title: chrome.axisTitle(isMobile ? undefined : 'Employment'),
      tickformat: ',.0f',
      side: 'right',
      overlaying: 'y',
      showgrid: false,
      ...chrome.axisDefaults
    } : undefined,
    yaxis3: {
      overlaying: 'y',
      range: [0, 1],
      showgrid: false,
      showticklabels: false,
      zeroline: false
    },
    hovermode: 'x unified',
    hoverlabel: chrome.hoverlabel,
    showlegend: true,
    legend: chrome.legend(),
    margin: isMobile
      ? { l: 48, r: showEmployment ? 48 : 16, t: 48, b: 88 }
      : { l: 80, r: showEmployment ? 88 : 24, t: 60, b: 60 },
    paper_bgcolor: chrome.paper_bgcolor,
    plot_bgcolor: chrome.plot_bgcolor,
    font: chrome.font,
    annotations: recessionAnnotations(recessionYears, colors, { enabled: !isMobile })
  };

  const config = {
    displayModeBar: !isMobile,
    modeBarButtonsToAdd: [
      'pan2d',
      'autoScale2d',
      'resetScale2d',
      'zoomIn2d',
      'zoomOut2d'
    ],
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false,
    responsive: true,
    toImageButtonOptions: {
      format: 'png',
      filename: `${derivedOccupationTitle.replace(/[^a-z0-9]/gi, '_')}_salary_trends`,
      height: 500,
      width: 800,
      scale: 2
    }
  };

  // Calculate trend statistics
  const latestYear = Math.max(...years);
  const earliestYear = Math.min(...years);
  const latestSalary = wageHistory.find(d => d.year === latestYear)?.a_median || 0;
  const earliestSalary = wageHistory.find(d => d.year === earliestYear)?.a_median || 0;
  const totalGrowth = earliestSalary > 0 ? ((latestSalary - earliestSalary) / earliestSalary) * 100 : 0;
  const annualGrowth = Math.pow(latestSalary / earliestSalary, 1 / (latestYear - earliestYear)) - 1;

  return (
    <Card className="relative">
      <CardHeader>
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <CardTitle className="min-w-0 flex-1 text-lg leading-snug break-words">{title}</CardTitle>
          <ChartExportButton
            chartId="interactive-timeseries-chart"
            filename={`salary-trends-${derivedOccupationTitle?.replace(/[^a-z0-9]/gi, '_') || 'chart'}`}
          />
        </div>
        <CardDescription>
          Interactive analysis with {wageHistory.length} years of data
          {rppAdjusted && " • Adjusted for regional price differences"}
          {recessionYears.length > 0 && ` • ${recessionYears.length} recession periods highlighted`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 relative">
        {/* Summary Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricTile
            tone="purple"
            icon={DollarSign}
            badge="Latest"
            value={`$${latestSalary.toLocaleString()}`}
            label="Latest Salary"
          />
          <MetricTile
            tone={totalGrowth >= 0 ? 'green' : 'red'}
            icon={totalGrowth >= 0 ? TrendingUp : TrendingDown}
            badge="Total"
            value={`${totalGrowth >= 0 ? '+' : ''}${totalGrowth.toFixed(1)}%`}
            label="Total Growth"
          />
          <MetricTile
            tone={annualGrowth >= 0 ? 'green' : 'red'}
            icon={annualGrowth >= 0 ? TrendingUp : TrendingDown}
            badge="Annual"
            value={`${annualGrowth >= 0 ? '+' : ''}${(annualGrowth * 100).toFixed(1)}%`}
            label="Annual Growth"
          />
          <MetricTile
            tone="blue"
            icon={Calendar}
            badge="Range"
            value={`${earliestYear}–${latestYear}`}
            label="Data Period"
            compactValue
          />
        </div>

        {/* Interactive Chart */}
        <div id="interactive-timeseries-chart" className="h-96 min-w-0 w-full overflow-x-auto">
          <Plot
            data={traces}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            key={`${themeKey}-${isMobile ? 'mobile' : 'desktop'}`}
          />
        </div>
      </CardContent>
    </Card>
  );
};